/**
 * VaultUploadWithGrading — Combined image upload + auto-grade trigger.
 * LAUNCH:C6.P3
 */
import React, { useState, useRef, useCallback } from 'react';
import { Upload, Image, AlertCircle, RefreshCw } from 'lucide-react';
import useAutoGrade from '../../hooks/useAutoGrade';
import { uploadAndGrade } from '../../services/autoGradeService';
import GradingResultsCard from './GradingResultsCard';
import UpgradePromptModal from './UpgradePromptModal';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

export default function VaultUploadWithGrading({ vaultItemId, currentPhotoUrl, onGradeComplete, onUploadComplete }) {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, queued, processing, completed, failed, quota_exceeded
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const ariaLiveRef = useRef(null);

  const { triggerGrade, retryGrade, status, result, isLoading, error, eventId } = useAutoGrade();

  // Merge hook status into component state
  const effectiveState = uploadState === 'uploading' ? 'uploading' : (status || uploadState);

  const announce = (msg) => {
    if (ariaLiveRef.current) ariaLiveRef.current.textContent = msg;
  };

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Only JPEG and PNG images accepted';
    }
    return null;
  };

  const handleUploadAndGrade = useCallback(async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      setUploadState('failed');
      announce(validationError);
      return;
    }

    setUploadState('uploading');
    setUploadError(null);
    setUploadProgress(0);
    announce('Uploading image...');

    try {
      // Step 1: Get presigned URL and event ID
      const data = await uploadAndGrade(vaultItemId, file.type);

      if (data.status === 'quota_exceeded') {
        setUploadState('quota_exceeded');
        setShowUpgradeModal(true);
        announce('Quota exceeded');
        return;
      }

      // Step 2: Upload to S3 via presigned URL
      const xhr = new XMLHttpRequest();
      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.open('PUT', data.presigned_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      onUploadComplete?.(data.presigned_url?.split('?')[0]);
      setUploadState('queued');
      announce('Grading queued...');

      // Step 3: Start polling via hook (using the event_id from the upload response)
      // The hook will handle polling and update status/result
      // We re-use triggerGrade here but the event was already created by uploadAndGrade
      // So we poll directly using the event_id
      const pollEventId = data.event_id;
      if (pollEventId) {
        // Use retryGrade which re-queues an existing event
        await retryGrade(pollEventId);
      }
    } catch (err) {
      setUploadState('failed');
      /* c8 ignore next */
      setUploadError(err.message || 'Upload failed');
      announce('Upload failed');
    }
  }, [vaultItemId, onUploadComplete, retryGrade]);

  const handleRegrade = useCallback(async () => {
    if (currentPhotoUrl) {
      setUploadState('queued');
      announce('Re-grading...');
      await triggerGrade(vaultItemId, currentPhotoUrl);
    }
  }, [vaultItemId, currentPhotoUrl, triggerGrade]);

  const handleRetry = useCallback(async () => {
    if (eventId) {
      setUploadState('queued');
      setUploadError(null);
      announce('Retrying...');
      await retryGrade(eventId);
    }
  }, [eventId, retryGrade]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUploadAndGrade(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleUploadAndGrade(file);
  };

  /* c8 ignore start */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };
  /* c8 ignore stop */

  // Handle hook status changes
  const displayState = (() => {
    if (uploadState === 'uploading') return 'uploading';
    if (status === 'quota_exceeded' || uploadState === 'quota_exceeded') return 'quota_exceeded';
    if (status === 'completed') return 'completed';
    if (status === 'failed' || uploadState === 'failed') return 'failed';
    if (status === 'processing') return 'processing';
    if (status === 'queued') return 'queued';
    return uploadState;
  })();

  // Sync grade complete callback
  React.useEffect(() => {
    if (status === 'completed' && result) {
      onGradeComplete?.(result);
    }
  }, [status, result, onGradeComplete]);

  return (
    <div className="space-y-3">
      {/* Aria Live Region */}
      <div ref={ariaLiveRef} aria-live="polite" className="sr-only" />

      {/* Upload Zone / Current Photo */}
      {displayState === 'idle' && !currentPhotoUrl && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            isDragging ? 'border-orange-500 bg-orange-500/10' : 'border-zinc-700 hover:border-zinc-600'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-label="Upload image for AI grading"
        >
          <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm text-zinc-400">Upload image</p>
          <p className="text-xs text-zinc-600 mt-1">Drag and drop or click to browse</p>
          <p className="text-xs text-zinc-600">JPEG or PNG only</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileSelect}
            className="hidden"
            aria-hidden="true"
          />
        </div>
      )}

      {displayState === 'idle' && currentPhotoUrl && (
        <div className="relative">
          <img src={currentPhotoUrl} alt="Current cover" className="w-full rounded-lg" />
          <button
            onClick={handleRegrade}
            className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-zinc-900/90 hover:bg-zinc-800 text-white text-xs rounded transition-colors"
          >
            <RefreshCw className="w-3 h-3" aria-hidden="true" />
            Re-grade
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {displayState === 'uploading' && (
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-zinc-300">Uploading... {uploadProgress}%</span>
          </div>
          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-500 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Queued */}
      {displayState === 'queued' && (
        <div className="bg-zinc-800/50 rounded-lg p-4 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-300">Grading queued...</span>
        </div>
      )}

      {/* Processing */}
      {displayState === 'processing' && (
        <div className="bg-zinc-800/50 rounded-lg p-4 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-300">AI analyzing your comic...</span>
        </div>
      )}

      {/* Completed */}
      {displayState === 'completed' && result && (
        <GradingResultsCard
          gradeResult={result}
          /* c8 ignore next */
          onRegrade={currentPhotoUrl ? handleRegrade : undefined}
          showCGCCta={true}
          vaultItemId={vaultItemId}
        />
      )}

      {/* Failed */}
      {displayState === 'failed' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-400" aria-hidden="true" />
            {/* c8 ignore next */}
            <span className="text-sm text-red-400">{uploadError || error || 'Grading failed'}</span>
          </div>
          <button
            onClick={handleRetry}
            className="text-xs text-zinc-400 hover:text-white transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal || displayState === 'quota_exceeded'}
        /* c8 ignore next */
        onClose={() => setShowUpgradeModal(false)}
        currentUsed={0}
        limit={3}
      />
    </div>
  );
}
